import type { UseFetchOptions, UseFetchReturn } from "@vueuse/core";
import { computed, type MaybeRef, type MaybeRefOrGetter, toValue } from "vue";

import type { SubmitCommentParams } from "@/composition/api/commentsApi";
import type {
  DpaNewsComment,
  DpaNewsCommentReply,
  DpaNewsEnvelope,
  DpaNewsMessageAuthor,
} from "@/types/messenger/comments";
import { commentsApiClient } from "@/utils/apiClients.ts";

function useEnvelope(
  guid: MaybeRefOrGetter<string>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsEnvelope | null> {
  const url = computed(() => `envelopes/byValueUnit/${toValue(guid)}`);
  return commentsApiClient(url, options).json();
}

function useAllEnvelopes(
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsEnvelope[]> {
  return commentsApiClient("envelopes", {
    ...options,
    afterFetch(ctx) {
      return {
        ...ctx,
        data: ctx.data.map((envelope: DpaNewsEnvelope) => {
          return {
            ...envelope,
            isNew: false,
          };
        }),
      };
    },
  }).json();
}

function useSubmitComment(
  comment: MaybeRef<{
    valueUnitGuid: string;
    valueUnitSupplier: string;
    commentBody: string;
    commentAuthor: DpaNewsMessageAuthor;
  }>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsComment> {
  return commentsApiClient("", options)
    .post(() => {
      const commentValue = toValue(comment);
      return {
        ...commentValue,
        valueUnitSupplier: "DPA_NEWS",
        ...commentValue.commentAuthor,
      };
    })
    .json();
}

function useSubmitReply(
  reply: MaybeRef<
    Omit<SubmitCommentParams, "valueUnitGuid" | "valueUnitSupplier"> & {
      commentId: string;
    }
  >,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsCommentReply> {
  const url = computed(() => `${toValue(reply).commentId}/replies`);
  return commentsApiClient(url, options)
    .post(() => {
      const replyValue = toValue(reply);
      return {
        replyBody: replyValue.commentBody,
        ...replyValue.commentAuthor,
      };
    })
    .json();
}

export { useAllEnvelopes, useEnvelope, useSubmitComment, useSubmitReply };
